<template>
  <wizard-container
    :class="{
      'search-step-open': searchStepOpen,
    }"
  >
    <form :action="formActionUrl" @submit="formSubmit">
      <translate
        v-if="$route.query.error === 'search-error' || showSearchError"
        tag="p"
        class="err-msg"
      >
        Unfortunately there was an error: please try again or amend your search
      </translate>
      <location-section
        v-show="showSummary"
        :input-value="inputValue"
        :focused="locationInputFocused"
        :force-suggestions-open="forceSuggestionsOpen"
        :input-placeholder="inputPlaceholder"
        :override-hide-clearable="overrideHideClearable"
        :has-bounds="hasBounds"
        :search-input="locationSuggestionsStore?.context?.input || ''"
        :selected-location="selectedLocation"
        :selected-location-type="selectedLocationType"
        :suggestions="locationSuggestionsStore?.context?.suggestions || {}"
        :show-input-only="!wizardOpen"
        @input-changed="onLocationInputChanged"
        @location-selected="locationSelected"
        @clear-input="clearInput"
        @update-suggestions="updateSuggestions($event)"
        @modify-within="modifyWithin($event)"
        @autosuggestion-closed="forceSuggestionsOpen = false"
        @focus="locationSectionFocussed"
        @blur="inputIsFocused = false"
        @click.passive="clickHandler"
      />
      <accommodation-wizard
        :open="matches('categories')"
        :show-summary="showSummary"
        :category-ids="categoryIds"
        :enabled="true"
        :search-filters="searchFilters"
        :show-counts="searchWizardStore.showCount"
        @open="openCategories()"
        @close="cancelEdit()"
        @cancel="cancelEdit()"
        @change-category-ids="categoryIds = $event"
      />
      <dates-wizard
        :open="matches('dates')"
        :show-summary="showSummary"
        :dates="dates"
        @open="openDates()"
        @cancel="cancelEdit()"
        @change-dates="dates = $event"
        @clear-dates="clearDates"
      />
      <guests-wizard
        :open="matches('guests')"
        :enabled="guestsEnabled"
        :clear="isClearSearch"
        :show-summary="showSummary"
        :guests="guests"
        class="guests-wizard"
        @open="openGuests()"
        @cancel="cancelEdit()"
        @change-guests="guests = $event"
        @click="searchWizardStore.isClearSearch = false"
      />

      <div v-if="showSummary" class="campsite-search-wizard-main-actions">
        <base-submit-button
          data-test="search-wizard-submit"
          class="primary asblock button"
          :submitting="
            formSubmitting ||
            searchStore.states.includes('searching') ||
            searchStore.states.includes('priceSearching')
          "
          :class="{ disabled: !searchEnabled }"
        >
          <translate v-if="editMode"> Update </translate>
          <translate v-else> Search </translate>
          <translate
            v-if="showCount"
            :tt="{ count: currentCount }"
            class="count"
          >
            ({count, number})
          </translate>
          <template #submittingText>
            <translate>Searching...</translate>
          </template>
        </base-submit-button>
        <wizard-footer
          class="search-buttons"
          @cancel="close()"
          @clear="clear()"
        />
      </div>
    </form>
    <slot />
  </wizard-container>
</template>
<script lang="ts">
import { navigateTo } from 'nuxt/app'
import { isNull, isUndefined } from 'lodash-es'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { getUrlPrefix } from '../../../../lang/locales'
import { GeoCircle } from '../../../../utility/geo/GeoLocation'
import { SearchParty } from '../../../search/schemas'
import { ArriveDepartIso } from '../../../search/types'
import { SearchLinkGenerator } from '../../../url/generators/SearchLinkGenerator'
import { LocationWizardData } from '../../domain/LocationWizardData'
import { SearchWizardFilters } from '../domain/SearchWizardFilters'
import { CampsitesSearchWizardMainProps } from './CampsitesSearchWizardMainProps'

@Component({
  emits: ['ready', 'wizardOpened'],
  setup() {
    const searchStore = useSearchStoreSetup(usePinia())
    const searchWizardStore = useSearchWizardStore(usePinia())
    return { searchWizardStore, searchStore }
  },
})
class CampsitesSearchWizardMain
  extends Vue
  implements CampsitesSearchWizardMainProps
{
  searchStore: ReturnType<typeof useSearchStoreSetup>
  searchWizardStore: ReturnType<typeof useSearchWizardStore>
  showSearchError = false
  formSubmitting = false
  inputValue = ''
  currentCount: number | null = null

  @Prop({ default: false })
  showOpenCategories: boolean

  @Prop()
  query?: string

  locationSuggestionsStore?: ReturnType<typeof useLocationSuggestionsStore>
  inputIsFocused = false

  get context() {
    return this.searchWizardStore.context
  }

  editMode = false

  get isClearSearch() {
    return this.searchWizardStore.isClearSearch
  }

  forceSuggestionsOpen = false

  get showCount() {
    return this.searchWizardStore.showCount && !isNull(this.currentCount)
  }

  get totalCount() {
    return this.searchWizardStore.totalCounts
  }

  @Watch('totalCount', { immediate: true })
  onTotalCountChange() {
    if (!isUndefined(this.totalCount)) {
      this.currentCount = this.totalCount
    } else {
      this.currentCount = null
    }
  }

  updateSuggestions(input: string) {
    this.inputValue = input
    this.locationSuggestionsStore?.send({ type: 'SET_INPUT', input })
  }

  async locationSelected(location: LocationWizardData, term) {
    /* eslint-disable @typescript-eslint/no-unsafe-assignment -- term parameter is untyped */
    this.locationSuggestionsStore?.send({
      type: 'SELECT_LOCATION',
      name: term || location.name,
    })
    /* eslint-enable @typescript-eslint/no-unsafe-assignment */

    // If custom location is selected we need to retrieve point from mapbox
    if (location.dataType === 'custom') {
      try {
        const suggestionWithPoint =
          await this.locationSuggestionsStore?.loadGeoPointForLocation(location)
        location.point = suggestionWithPoint?.point
      } catch {
        console.error(
          'CampsiteSearchWizardMain: locationSelected->loadGeoPointForLocation failed',
        )
        this.clearInput()
        this.inputValue = ''
        this.showSearchError = true
        return
      }
    }

    /* eslint-disable @typescript-eslint/no-unsafe-assignment -- term parameter is untyped */
    this.searchWizardStore.send('SET_LOCATION', {
      data: location,
      inputValue: term,
    })
    /* eslint-enable @typescript-eslint/no-unsafe-assignment */
    this.cancelEdit()
    this.updateCounts()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type term parameter properly
    this.inputValue = term || location.name || ''
  }

  onLocationInputChanged(inputValue: string) {
    this.inputValue = inputValue
  }

  async mounted() {
    this.locationSuggestionsStore = useLocationSuggestionsStore(usePinia())
    this.locationSuggestionsStore.send({
      type: 'SET_INITIAL_INPUT',
      input: this.searchInputValue,
    })
    this.searchWizardStore.service.onTransition((state, event) => {
      this.showSearchError = false
      if (event.type === 'CLEAR_SEARCH_PARAMS') {
        this.inputValue = ''
        this.locationSuggestionsStore?.send({
          type: 'SET_INPUT',
          input: '',
        })
        this.currentCount = null
      }
    })
    this.forceSuggestionsOpen = !!this.query
    this.isSearchWizardOpenChanged()
    if (this.showOpenCategories) {
      this.openCategories()
    }
    await new Promise((resolve) => {
      setTimeout(resolve, 1000)
    })
    this.forceSuggestionsOpen = false
  }

  clearInput() {
    this.searchWizardStore.send({ type: 'CLEAR_INPUT' })
    this.locationSuggestionsStore?.send({
      type: 'SET_INPUT',
      input: '',
    })
    this.currentCount = null
  }

  get inputPlaceholder() {
    return this.$t(
      this.inputIsFocused
        ? 'Where are you going?'
        : this.searchWizardStore.inputPlaceholder,
    ).toString()
  }

  get overrideHideClearable() {
    return Boolean(
      this.context?.searchInputValue?.type === 'LOCATION' &&
        !this.searchWizardStore.locationInputHasValue,
    )
  }

  get searchInputValue() {
    return (
      (this.context?.searchInputValue?.type === 'CUSTOM' &&
        this.context?.searchInputValue?.text) ||
      ''
    )
  }

  set searchInputValue(value: string) {
    // this.searchWizardStore.send({ type: 'SET_INPUT_VALUE', value })
  }

  get searchFilters(): SearchWizardFilters {
    return this.searchWizardStore.wizardFilters
  }

  get wizardOpen() {
    return this.searchWizardStore.openOnFocus
      ? this.searchWizardStore.isSearchWizardOpen
      : true
  }

  @Watch('searchWizardStore.isSearchWizardOpen')
  isSearchWizardOpenChanged() {
    if (!this.searchWizardStore.isSearchWizardOpen) {
      return
    }
    this.openSuggestions()
  }

  private openSuggestions() {
    if (this.query) {
      this.openLocation()
      this.updateSuggestions(this.query)
    }
  }

  get showSummary() {
    return this.matches('idle')
  }

  openLocation() {
    if (!this.searchWizardStore.isSearchWizardOpen) {
      setTimeout(() => {
        this.searchWizardStore.isSearchWizardOpen = true
        this.searchWizardStore.send('FOCUS_LOCATION')
      })
    }
  }

  openCategories() {
    this.searchWizardStore.send('EDIT_CATEGORIES')
  }

  get categoryIds(): string[] {
    return this.searchWizardStore.wizardFilters.categoryIds || []
  }

  set categoryIds(value: string[]) {
    this.searchWizardStore.send({
      type: 'SET_CATEGORIES',
      categoryIds: value,
    })
    this.updateCounts()
  }

  clickHandler() {
    this.openLocation()
  }

  get locationInputFocused(): boolean {
    return (
      this.matches('idle.locationFocus') &&
      this.searchWizardStore.isSearchWizardOpen
    )
  }

  openDates() {
    this.searchWizardStore.send('EDIT_DATES')
  }

  get dates(): ArriveDepartIso | undefined {
    return this.searchWizardStore?.wizardFilters?.dates
  }

  set dates(dates: ArriveDepartIso | undefined) {
    const newDates = dates && dates.arrive ? dates : null
    this.searchWizardStore.send({
      type: 'SET_DATES',
      dateRange: newDates,
    })
    this.updateCounts()
  }

  get hasBounds() {
    return !!this.searchWizardStore?.wizardFilters?.bounds
  }

  openGuests() {
    this.searchWizardStore.send('EDIT_GUESTS')
  }

  get guests(): SearchParty {
    return (
      this.searchWizardStore?.wizardFilters?.party || {
        adults: 2,
        childAges: [],
        dogs: 0,
      }
    )
  }

  set guests(guests: SearchParty) {
    this.searchWizardStore.send({ type: 'SET_GUESTS', guests })
    this.updateCounts()
  }

  get guestsEnabled() {
    return !!(this.dates?.arrive && this.dates?.depart)
  }

  get area(): GeoCircle | undefined {
    return this.searchWizardStore?.wizardFilters?.area
  }

  get searchEnabled() {
    /* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call -- Route name includes() call is safe */
    return (
      this.searchWizardStore.isSearchEnabled ||
      this.$route.name === 'index' ||
      this.$route.name?.includes('searchedit')
    )
    /* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call */
  }

  clearDates() {
    this.searchWizardStore.send({
      type: 'CLEAR_DATES',
    })
    this.updateCounts()
  }

  modifyWithin(within: number) {
    this.searchWizardStore.send('SET_WITHIN', { within })
    this.updateCounts()
  }

  cancelEdit() {
    this.searchWizardStore.send('CANCEL_EDIT')
  }

  async close() {
    // this is saying if on the homepage cloase it
    if (this.$route.name === 'index') {
      this.searchWizardStore.isSearchWizardOpen = false
      this.searchWizardStore.send('CLEAR_SEARCH_PARAMS')
      await this.$nextTick()
      this.$puScrollTo('[data-back-to-top]', {
        duration: 200,
      })
    } else {
      // otherwise just go back nav
      this.$router.go(-1)
    }
  }

  clear() {
    this.searchWizardStore.send('CLEAR_SEARCH_PARAMS')
    this.locationSuggestionsStore?.send({ type: 'SET_INPUT', input: '' })
    this.updateCounts()
  }

  locationSectionFocussed() {
    this.locationSuggestionsStore?.send('LOAD_RECENT_SEARCHES')
  }

  get selectedLocation() {
    return this.context.selectedLocation
  }

  get selectedLocationType() {
    return this.selectedLocation?.dataType || null
  }

  get searchStepOpen() {
    return !this.matches('idle')
  }

  matches(stateString: string): boolean {
    return this.searchWizardStore?.states.includes(stateString)
  }

  // TODO: move to machine
  updateCounts() {
    void this.searchWizardStore.updateCounts()
  }

  async formSubmit(event) {
    this.formSubmitting = true
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Event from form submission
      event.preventDefault()
      if (!this.searchEnabled) {
        return
      }

      if (
        this.locationSuggestionsStore?.stateStrings.includes(
          'loadingCustomGeoPoint',
        )
      ) {
        await this.waitForGeoPointToLoad()
      }

      if (this.locationSuggestionsStore?.stateStrings.includes('debouncing')) {
        await this.dispatchAsyncLocationSelection()
      }
      await navigateTo({
        path: this.formActionUrl,
        query: this.getSearchURL().query as Record<
          string,
          string | string[] | undefined
        >,
      })
    } catch {
      this.formSubmitting = false
    }
  }

  async dispatchAsyncLocationSelection() {
    const suggestion =
      await this.locationSuggestionsStore?.dispatchAsyncLocationSelection()
    if (suggestion) {
      await this.locationSelected(suggestion, this.inputValue)
    }
  }

  async waitForGeoPointToLoad() {
    await this.locationSuggestionsStore?.waitForMachineIdle()
    await this.$nextTick()
  }

  get formActionUrl() {
    const langPrefix = getUrlPrefix(this.$route.params.lang)
    if (this.selectedLocation?.dataType === 'campsite') {
      return `${langPrefix}/campsites/${this.selectedLocation.hierarchyPath}${this.selectedLocation.slug}`
    }
    const searchUrlPath = this.getSearchURL().path
    const path = `${langPrefix}${searchUrlPath}`
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Route name type needs refinement
    if (this.$route.name?.includes('searcheditmaps')) {
      return path.replace('/search/', '/maps/')
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Route name type needs refinement
    if (this.$route.name?.includes('searchnew')) {
      return path.replace('/search/', '/searchnew/')
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Route name type needs refinement
    if (this.$route.name?.includes('searchold')) {
      return path.replace('/search/', '/searchold/')
    }
    return path
  }

  getSearchURL() {
    const urlGenerator = new SearchLinkGenerator()
    const urlData = urlGenerator.getUrlData({
      category: this.$route.name === 'searcheditmaps-all' ? 'maps' : 'search',
      excludeType: 'searchWizard',
      filters: this.searchFilters,
    })

    return urlData
  }

  beforeUnmount() {
    if (this && this.searchWizardStore) {
      this.searchWizardStore.isSearchWizardOpen = false
    }
  }
}

export default toNative(CampsitesSearchWizardMain)
</script>
<style scoped>
.count {
  font-weight: normal;
  color: #b8ddbf;
}

.guests-wizard {
  margin-bottom: 2em;
}

.err-msg {
  text-align: center;
  color: var(--colour-red);
  font-size: var(--body);
  margin-bottom: 1rem;
}

.disabled-search-button-text {
  text-align: center;
  color: var(--colour-red);
  font-size: var(--body);
  margin-bottom: 0.5em;
}
</style>
